import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { translate } from "@/i18n";

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lang, setLangState] = useState(localStorage.getItem("dp_lang") || "en");

  const t = useCallback((key, vars) => translate(lang, key, vars), [lang]);

  const setLang = useCallback((l) => {
    setLangState(l);
    localStorage.setItem("dp_lang", l);
    if (localStorage.getItem("dp_token")) {
      api.put("/auth/language", { preferred_language: l }).catch(() => {});
    }
  }, []);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setQuota(data.quota);
      return data;
    } catch (e) {
      setUser(null);
      setQuota(null);
      return null;
    }
  }, []);

  useEffect(() => {
    (async () => {
      const token = localStorage.getItem("dp_token");
      if (token) {
        const data = await refreshMe();
        if (data?.user?.preferred_language && !localStorage.getItem("dp_lang")) {
          setLangState(data.user.preferred_language);
        }
      }
      setLoading(false);
    })();
  }, [refreshMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("dp_token", data.token);
    await refreshMe();
    return data;
  };

  const register = async (email, password) => {
    const { data } = await api.post("/auth/register", { email, password });
    localStorage.setItem("dp_token", data.token);
    await refreshMe();
    return data;
  };

  const logout = () => {
    localStorage.removeItem("dp_token");
    setUser(null);
    setQuota(null);
  };

  return (
    <AppContext.Provider
      value={{ user, quota, loading, lang, setLang, t, login, register, logout, refreshMe, setQuota }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
}
